import React, { MouseEvent } from "react"
import { Tr, Td } from "@chakra-ui/react"
import { Row, flexRender } from "@tanstack/react-table"
import { VirtualItem } from "@tanstack/react-virtual"
import { GuestSummary } from "sharedTypes"
import { columnConfigs } from "./columnsConfig"

type ColumnMeta = {
  align?: string;
  icon?: string;
}

type Props = {
  virtualItem: VirtualItem;
  row: Row<GuestSummary>;
  isSelected: boolean;
  isActive: boolean;
  getColumnWidth: (columnId: string) => string;
  onClick: (event: MouseEvent<HTMLElement>, guest: GuestSummary) => void;
  cellPaddingX: number;
}

const DataTableRow = ({
  virtualItem,
  row,
  isSelected,
  isActive,
  getColumnWidth,
  onClick,
  cellPaddingX,
}: Props) => {
  const highlighted = isSelected || isActive
  const rowBg = highlighted ? "blue.50" : "white"
  const hoverBg = highlighted ? "blue.50" : "gray.50"

  return (
    <Tr
      key={virtualItem.key}
      position="absolute"
      top={0}
      left={0}
      width="100%"
      height={`${virtualItem.size}px`}
      transform={`translateY(${virtualItem.start}px)`}
      display="flex"
      borderBottomWidth="1px"
      borderBottomStyle="solid"
      borderBottomColor="gray.200"
      bg={highlighted ? "blue.50" : undefined}
      _hover={{ bg: highlighted ? undefined : "gray.50" }}
      cursor="pointer"
      onClick={(e) => onClick(e, row.original)}
      zIndex={1}
      role="row"
      aria-rowindex={virtualItem.index + 2}
      aria-selected={isSelected}
      aria-current={isActive ? "true" : undefined}
      className="data-table-row"
    >
      {row.getVisibleCells().map((cell) => {
        const meta = cell.column.columnDef.meta as ColumnMeta | undefined
        const isNameColumn = cell.column.id === "name"

        return (
          <Td
            key={cell.id}
            width={getColumnWidth(cell.column.id)}
            px={cellPaddingX}
            py={2}
            display="flex"
            alignItems="center"
            textAlign={meta?.align === "center" ? "center" : undefined}
            minWidth={columnConfigs[cell.column.id]?.minWidth}
            flexShrink={0}
            boxSizing="border-box"
            role="cell"
            borderRightWidth="1px"
            borderRightColor="gray.100"
            position={isNameColumn ? "sticky" : undefined}
            left={isNameColumn ? 0 : undefined}
            zIndex={isNameColumn ? 1 : undefined}
            bg={isNameColumn ? rowBg : undefined}
            sx={isNameColumn ? {
              ".data-table-row:hover &": { bg: hoverBg },
            } : undefined}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Td>
        )
      })}
    </Tr>
  )
}

export default React.memo(DataTableRow, (prev, next) => (
  prev.virtualItem.index === next.virtualItem.index
  && prev.virtualItem.start === next.virtualItem.start
  && prev.virtualItem.size === next.virtualItem.size
  && prev.isSelected === next.isSelected
  && prev.isActive === next.isActive
  && prev.row.original === next.row.original
  && prev.getColumnWidth === next.getColumnWidth
))
